import { useId } from "react";

import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  Flex,
  Slider,
  Text,
} from "@radix-ui/themes";

import type { ClosePositionDialogController } from "./ClosePositionDialog.controller";
import type { PositionsWidgetRow } from "./PositionsWidget.types";

export interface ClosePositionDialogPresentationProps
  extends ClosePositionDialogController {
  open: boolean;
  positions: PositionsWidgetRow[];
  submitting: boolean;
  errorMessage?: string;
  onOpenChange: (open: boolean) => void;
}

const formatCloseQuantity = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

export const ClosePositionDialogPresentation = ({
  open,
  positions,
  submitting,
  errorMessage,
  onOpenChange,
  single,
  maxQty,
  showBulkRatio,
  partialQty,
  bulkRatio,
  canSubmit,
  onPartialQtyChange,
  onBulkRatioChange,
  onSubmit,
}: ClosePositionDialogPresentationProps) => {
  const cancelOrdersId = useId();
  const symbols = positions
    .slice(0, 8)
    .map((position) => position.symbol)
    .join(", ")
    .concat(positions.length > 8 ? "…" : "");

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="28rem">
        <form noValidate onSubmit={onSubmit}>
          <Dialog.Title>Close position</Dialog.Title>
          <Dialog.Description size="2" mb="3" color="gray">
            This immediately closes {positions.length} open position
            {positions.length === 1 ? "" : "s"}
            {showBulkRatio ? ` at ${bulkRatio}%` : ""}. It is not a draft
            ticket.
          </Dialog.Description>

          {symbols ? (
            <Text size="2" weight="medium" mb="3">
              {symbols}
            </Text>
          ) : null}

          {single && maxQty > 0 && !showBulkRatio ? (
            <Flex direction="column" gap="2" mb="3">
              <Text size="1" color="gray">
                Quantity to close (max {formatCloseQuantity(maxQty)})
              </Text>
              <Slider
                size="1"
                min={0}
                max={maxQty}
                step={Number.isInteger(maxQty) ? 1 : 0.01}
                value={[partialQty]}
                aria-label="Close quantity"
                onValueChange={([next]) => {
                  if (typeof next === "number") {
                    onPartialQtyChange(next);
                  }
                }}
              />
              <Text size="2" className="atd-numeric">
                {formatCloseQuantity(partialQty)} /{" "}
                {formatCloseQuantity(maxQty)}
              </Text>
            </Flex>
          ) : null}

          {showBulkRatio ? (
            <Flex
              direction="column"
              gap="2"
              mb="3"
              data-testid="bulk-close-ratio"
            >
              <Text size="1" color="gray">
                Close ratio (applied to each selected position)
              </Text>
              <Slider
                size="1"
                min={1}
                max={100}
                step={1}
                value={[bulkRatio]}
                aria-label="Close ratio"
                onValueChange={([next]) => {
                  if (typeof next === "number") {
                    onBulkRatioChange(next);
                  }
                }}
              />
              <Text size="2" className="atd-numeric">
                {bulkRatio}%
              </Text>
            </Flex>
          ) : null}

          {errorMessage ? (
            <Callout.Root color="red" size="1" mb="3">
              <Callout.Text>{errorMessage}</Callout.Text>
            </Callout.Root>
          ) : null}

          <Flex asChild align="center" gap="2" mb="4" title="Coming soon">
            <label htmlFor={cancelOrdersId}>
              <Checkbox
                id={cancelOrdersId}
                size="1"
                checked={false}
                disabled
                onCheckedChange={() => undefined}
              />
              <Text size="2" color="gray">
                Cancel open orders for{" "}
                {positions.length === 1 ? "this symbol" : "these symbols"}{" "}
                (coming soon)
              </Text>
            </label>
          </Flex>

          <Flex gap="2" justify="end">
            <Dialog.Close>
              <Button
                type="button"
                variant="soft"
                color="gray"
                disabled={submitting}
              >
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="submit"
              variant="solid"
              disabled={!canSubmit || submitting}
            >
              {submitting ? "Closing…" : "Close position"}
            </Button>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};
